import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api } from '../api.js';
import { estadoAccion, etapaActiva, type Obra } from './modelo.js';
import { puntuar } from './puntuacion.js';

/**
 * EL MAPA DE OBRAS, SOBRE UN MAPA DE VERDAD.
 *
 * Antes era un SVG dibujado a mano: el contorno de la isla y las zonas como
 * circulos. Se eligio asi para no depender de nadie, y aguanto mientras las
 * obras se situaban «por zona». Dejo de aguantar en cuanto se usa de verdad:
 * para ir a ver una obra hace falta la CALLE, y seis zonas del suroeste caben
 * en quince kilometros donde el dibujo solo enseñaba manchas.
 *
 * Ahora las obras van encima de un callejero con teselas, que se amplia hasta el
 * numero del portal. Lo que eso cuesta, dicho claro:
 *
 *   - Las teselas las sirve un tercero. Por defecto OpenStreetMap, que no pide
 *     credenciales; con clave propia se puede poner satelite (Google, MapTiler,
 *     Esri) sin tocar codigo, porque la direccion de las teselas es una
 *     variable de entorno.
 *   - El navegador pide al proveedor los cuadros del mapa que se ven. La obra
 *     no viaja: viaja la zona que se esta mirando, como en cualquier mapa.
 *   - Sin conexion con el proveedor, el mapa queda gris y las chinchetas se
 *     siguen viendo y pulsando.
 *
 * La chincheta es la misma de siempre: anillo de progreso, porcentaje dentro y
 * el distintivo del estado comercial. Cambia el fondo, no el lenguaje.
 */

interface Props {
  obras: Obra[];
  seleccionada: string | null;
  idioma: Idioma;
  hoy: string;
  onSeleccionar: (id: string | null) => void;
  onAbrirDetalle: (id: string) => void;
}

/** Mallorca entera, para cuando no hay ninguna obra que encuadrar. */
const ISLA: L.LatLngBoundsExpression = [
  [39.26, 2.33],
  [39.96, 3.46],
];

const RADIO = 21;
const PERIMETRO = 2 * Math.PI * RADIO;

function colorProgreso(progreso: number): string {
  if (progreso >= 85) return 'var(--verde)';
  if (progreso >= 50) return 'var(--t-acento)';
  if (progreso >= 20) return 'var(--info)';
  return 'var(--tinta-suave)';
}

/** El escudo del marcador, en el mismo lenguaje que tenia el mapa dibujado. */
function dibujoMarcador(obra: Obra, hoy: string, elegida: boolean): string {
  const p = puntuar(obra, hoy);
  const estado = estadoAccion(obra, hoy);
  const viva = etapaActiva(obra.etapa);
  const progreso = Math.max(0, Math.min(100, obra.progreso));
  const relleno = (progreso / 100) * PERIMETRO;
  const distintivo =
    viva && estado === 'VENCIDA'
      ? '<circle class="distintivo vencida" r="6" />'
      : viva && p.prioridad === 'ALTA'
        ? '<circle class="distintivo alta" r="5" />'
        : '';
  const clases = `marcador-obra${elegida ? ' elegido' : ''}${viva ? '' : ' cerrada'}`;
  return `
    <svg class="${clases}" viewBox="-26 -26 52 52" width="52" height="52" aria-hidden="true">
      <circle r="${RADIO}" class="marcador-fondo" />
      <circle r="${RADIO}" class="marcador-anillo" stroke="${colorProgreso(progreso)}"
              stroke-dasharray="${relleno.toFixed(1)} ${(PERIMETRO - relleno).toFixed(1)}"
              transform="rotate(-90)" />
      <text class="marcador-texto" text-anchor="middle" dy="0.34em">${progreso}</text>
      <g transform="translate(${RADIO - 4} ${-RADIO + 4})">${distintivo}</g>
    </svg>`;
}

interface Capa {
  url: string;
  atribucion: string;
  zoomMaximo: number;
}

/** El proveedor por defecto, el mismo que sirve la API si no hay otro puesto. */
const OSM: Capa = {
  url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
  atribucion: '© OpenStreetMap',
  zoomMaximo: 19,
};

interface ConfigMapa {
  calle: Capa;
  satelite: Capa | null;
}

export function MapaTeselas({
  obras,
  seleccionada,
  idioma,
  hoy,
  onSeleccionar,
  onAbrirDetalle,
}: Props) {
  const caja = useRef<HTMLDivElement>(null);
  const mapa = useRef<L.Map | null>(null);
  const marcadores = useRef(new Map<string, L.Marker>());
  const encuadrado = useRef(false);
  /** El encuadre que se quiere, en cuanto la caja tenga tamano de verdad. */
  const objetivo = useRef<L.LatLngBounds | null>(null);
  const [config, setConfig] = useState<ConfigMapa | null>(null);
  /*
    El mapa nace cuando llega su configuracion, DESPUES del primer dibujado. Sin
    este aviso, el efecto que pone las chinchetas ya habia pasado —y no volvia a
    pasar, porque las obras no cambian— y el mapa se quedaba vacio.
  */
  const [listo, setListo] = useState(false);

  // Las manos de los callbacks cambian en cada dibujado; el mapa se monta una
  // sola vez, asi que se leen por referencia.
  const acciones = useRef({ onSeleccionar, onAbrirDetalle, hoy });
  acciones.current = { onSeleccionar, onAbrirDetalle, hoy };

  useEffect(() => {
    let vivo = true;
    api
      .mapaConfig()
      .then((c) => {
        if (vivo) setConfig(c);
      })
      .catch(() => {
        // Si la configuracion no llega, el mapa sale igualmente con el
        // proveedor por defecto: quedarse sin mapa por eso seria peor.
        if (vivo) setConfig({ calle: OSM, satelite: null });
      });
    return () => {
      vivo = false;
    };
  }, []);

  // --- Montaje del mapa ------------------------------------------------------
  useEffect(() => {
    if (!caja.current || mapa.current || !config) return;
    const m = L.map(caja.current, {
      zoomControl: true,
      attributionControl: true,
      // El scroll de la pagina no se secuestra: se amplia con Ctrl o con los
      // botones, como en cualquier mapa incrustado.
      scrollWheelZoom: false,
      maxBounds: ISLA,
      maxBoundsViscosity: 0.4,
    }).fitBounds(ISLA);

    const calle = L.tileLayer(config.calle.url, {
      attribution: config.calle.atribucion,
      maxZoom: config.calle.zoomMaximo,
    }).addTo(m);

    if (config.satelite) {
      const satelite = L.tileLayer(config.satelite.url, {
        attribution: config.satelite.atribucion,
        maxZoom: config.satelite.zoomMaximo,
      });
      L.control
        .layers(
          { [t(idioma, 'obras.mapaCalle')]: calle, [t(idioma, 'obras.mapaSatelite')]: satelite },
          {},
          { position: 'topright' },
        )
        .addTo(m);
    }

    // Pulsar fuera de una chincheta cierra la ficha rapida.
    m.on('click', () => acciones.current.onSeleccionar(null));
    mapa.current = m;
    setListo(true);

    /*
      EL MAPA MIDE LO QUE MIDA SU CAJA, Y LA CAJA CAMBIA. Leaflet calcula el
      tamano al nacer; si nace mientras la pantalla se esta colocando —o si
      luego se abre la ficha rapida al lado— se queda con el tamano viejo y solo
      pinta un cuadro de mapa. Se le avisa de cada cambio.
    */
    const observador = new ResizeObserver(() => {
      m.invalidateSize();
      encuadrar();
    });
    observador.observe(caja.current);

    return () => {
      observador.disconnect();
      m.remove();
      mapa.current = null;
      marcadores.current.clear();
      encuadrado.current = false;
      setListo(false);
    };
  }, [config, idioma]);

  /**
   * Encuadra cuando se puede, no cuando toca.
   *
   * Si la caja aun no tiene ancho —la pantalla se esta colocando— cualquier
   * encuadre sale mal y el mapa se queda en el zoom del mundo entero. Se guarda
   * lo que se quiere ver y se aplica en cuanto hay sitio donde verlo.
   */
  const encuadrar = () => {
    const m = mapa.current;
    const ancho = caja.current?.clientWidth ?? 0;
    if (!m || ancho < 80 || encuadrado.current) return;
    const donde = objetivo.current;
    if (donde) {
      m.fitBounds(donde.pad(0.25), { maxZoom: 16 });
      encuadrado.current = true;
      return;
    }
    m.fitBounds(ISLA);
  };

  // --- Las chinchetas --------------------------------------------------------
  useEffect(() => {
    const m = mapa.current;
    if (!m) return;

    const vistos = new Set<string>();
    for (const obra of obras) {
      if (!Number.isFinite(obra.lat) || !Number.isFinite(obra.lon)) continue;
      if (obra.lat === 0 && obra.lon === 0) continue;
      vistos.add(obra.id);
      const elegida = obra.id === seleccionada;
      const icono = L.divIcon({
        html: dibujoMarcador(obra, hoy, elegida),
        className: 'chincheta-obra',
        iconSize: [52, 52],
        iconAnchor: [26, 26],
      });
      const existente = marcadores.current.get(obra.id);
      if (existente) {
        existente.setLatLng([obra.lat, obra.lon]).setIcon(icono);
        existente.setZIndexOffset(elegida ? 1000 : 0);
        continue;
      }
      const marca = L.marker([obra.lat, obra.lon], {
        icon: icono,
        title: obra.nombre,
        keyboard: true,
        alt: obra.nombre,
        zIndexOffset: elegida ? 1000 : 0,
      }).addTo(m);
      /*
        EL PRIMER CLIC ABRE LA FICHA RAPIDA; EL SEGUNDO, LA COMPLETA. Es lo que
        hacia el mapa dibujado, y lo que permite recorrerlo entero sin apartar
        la mano: clic, leo, clic, entro.
      */
      marca.on('click', (e) => {
        L.DomEvent.stopPropagation(e);
        const abierta = marcadores.current.get(obra.id)?.options.zIndexOffset === 1000;
        if (abierta) acciones.current.onAbrirDetalle(obra.id);
        else acciones.current.onSeleccionar(obra.id);
      });
      marcadores.current.set(obra.id, marca);
    }

    // Lo que ya no se ve —filtrado— se quita.
    for (const [id, marca] of marcadores.current) {
      if (vistos.has(id)) continue;
      marca.remove();
      marcadores.current.delete(id);
    }

    /*
      SE ENCUADRA UNA VEZ, sobre las obras que haya al abrir. Reencuadrar en
      cada filtrado haria saltar el mapa al tocar una pastilla y se perderia el
      sitio, que es justo lo que uno acaba de buscar a mano.
    */
    if (!encuadrado.current && vistos.size > 0) {
      const puntos = obras
        .filter((o) => vistos.has(o.id))
        .map((o) => [o.lat, o.lon] as [number, number]);
      objetivo.current = L.latLngBounds(puntos);
      encuadrar();
    }
  }, [obras, seleccionada, hoy, listo]);

  // La chincheta elegida se centra si se ha quedado fuera de la vista.
  useEffect(() => {
    const m = mapa.current;
    if (!m || !seleccionada) return;
    const marca = marcadores.current.get(seleccionada);
    if (marca && !m.getBounds().pad(-0.15).contains(marca.getLatLng())) {
      m.panTo(marca.getLatLng(), { animate: true });
    }
  }, [seleccionada]);

  return (
    <div className="mapa-marco">
      <div ref={caja} className="mapa-teselas" role="application" aria-label={t(idioma, 'obras.verMapa')} />
      <p className="suave mapa-pie">{t(idioma, 'obras.mapaPie')}</p>
    </div>
  );
}
